import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { Session, User } from "@supabase/supabase-js";

export type Profile = {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
};

type AuthState = {
  session: Session | null;
  user: User | null;
  profile: Profile | null;
  isAdmin: boolean;
  loading: boolean;
  refreshProfile: () => Promise<void>;
  signOut: () => Promise<void>;
};

const AuthContext = createContext<AuthState | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);

  const loadProfileAndRole = async (user: User) => {
    try {
      const [{ data: prof, error: profileError }, { data: roles, error: rolesError }] = await Promise.all([
        supabase
          .from("profiles")
          .select("id, first_name, last_name")
          .eq("id", user.id)
          .maybeSingle(),
        supabase.from("user_roles").select("role").eq("user_id", user.id),
      ]);

      if (profileError) {
        console.error("[auth] profile lookup failed", profileError);
      }

      if (rolesError) {
        console.error("[auth] role lookup failed", rolesError);
      }

      // Email is sourced from the authenticated session, not the profiles table
      // (the email column is no longer readable from the client for privacy).
      setProfile(
        prof
          ? ({
              id: prof.id,
              first_name: prof.first_name,
              last_name: prof.last_name,
              email: user.email ?? "",
            } as Profile)
          : null,
      );
      setIsAdmin(!!roles?.some((r) => r.role === "admin"));
    } catch (error) {
      console.error("[auth] profile/role bootstrap failed", error);
      setProfile(null);
      setIsAdmin(false);
    }
  };

  useEffect(() => {
    let cancelled = false;

    // Hard ceiling: regardless of what getSession / the auth listener does,
    // never keep the whole app behind a "Loading…" trophy for more than 1.5s.
    // If Supabase is slow, unreachable, or holding a stale token, we'd rather
    // render the (logged-out) UI and let the listener correct things later.
    const safetyTimer = setTimeout(() => {
      if (!cancelled) setLoading(false);
    }, 1500);

    // Set up listener FIRST, then check existing session
    const { data: sub } = supabase.auth.onAuthStateChange((_event, sess) => {
      if (cancelled) return;
      setSession(sess);
      setLoading(false);
      if (sess?.user) {
        // Defer to avoid deadlock with Supabase client
        const u = sess.user;
        setTimeout(() => {
          void loadProfileAndRole(u);
        }, 0);
      } else {
        setProfile(null);
        setIsAdmin(false);
      }
    });

    // Race getSession() against a short timeout so a hung network call
    // can't trap us on the loading screen. The auth listener above will
    // still fire with the real session whenever Supabase eventually responds.
    const sessionPromise = supabase.auth.getSession().then(
      ({ data: { session: sess } }) => sess,
      (err) => {
        console.error("[auth] getSession failed", err);
        return null;
      },
    );
    const timeoutPromise = new Promise<null>((resolve) =>
      setTimeout(() => resolve(null), 1500),
    );

    Promise.race([sessionPromise, timeoutPromise]).then((sess) => {
      if (cancelled) return;
      setSession(sess);
      if (sess?.user) {
        setLoading(false);
        void loadProfileAndRole(sess.user);
      } else {
        setProfile(null);
        setIsAdmin(false);
        setLoading(false);
      }
    });

    return () => {
      cancelled = true;
      clearTimeout(safetyTimer);
      sub.subscription.unsubscribe();
    };
  }, []);

  const refreshProfile = async () => {
    if (session?.user) await loadProfileAndRole(session.user);
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    window.location.href = "/";
  };

  return (
    <AuthContext.Provider
      value={{
        session,
        user: session?.user ?? null,
        profile,
        isAdmin,
        loading,
        refreshProfile,
        signOut,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
